import type { IVector3 } from '@seddi/math-v2';
import {
  AmbientLight,
  Clock,
  Color,
  CustomToneMapping,
  DepthTexture,
  FloatType,
  Object3D,
  PerspectiveCamera,
  RGBAFormat,
  Scene,
  SRGBColorSpace,
  Vector4,
  WebGLRenderTarget,
} from '@seddi/three';
import { EffectComposer } from '@seddi/three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from '@seddi/three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from '@seddi/three/examples/jsm/postprocessing/ShaderPass.js';
import { GammaCorrectionShader } from '@seddi/three/examples/jsm/shaders/GammaCorrectionShader.js';
import { Pointer } from '@seddi/ui-viewport-core';
import log from 'loglevel';
import { type CSSProperties, type RefObject, Component, createRef } from 'react';
import styled from 'styled-components';

import { assert } from '../../core/assert';
import { create as createObject } from '../../core/object3d';
import { Viewport } from '../../core/viewport';
import * as FlagsModule from '../../flags/flagsModule';
import { type FeatureFlagsState, FeatFlag } from '../../flags/flagTypes';
import { type SceneRenderable, create as createSceneRenderable } from '../../renderables/sceneRenderable';
import { ThreeMainRenderPass } from '../../renderer3/passes/threeMainRenderPass';
import { ThreeOutlinePass } from '../../renderer3/passes/threeOutlinePass';
import { ThreeRenderer } from '../../renderer3/threeRenderer';
import type { ResourceEvent } from '../../resources/resourceEvent';
import { ResourcesContext } from '../../resources/resourcesContext';
import { findWithCamera } from '../../scene/scene';
import { SceneGeometryModule } from '../../scene/sceneGeometryModule';
import type { SceneDesc } from '../../scene/sceneTypes';
import { SceneReconciler } from '../../sceneReconciler/sceneReconciler';
import type { VisibilityState } from '../../sceneReconciler/visibilityState';
import type { Theme } from '../../theme/themeTypes';
import { type SceneViewerConfig, create as createConfig } from '../../sceneViewerConfig';
import { GizmoModes } from './tools/gizmoTypes';
import type { ITool } from './tools/toolTypes';
import Camera from './camera';

const EventTypes = ['resourcechange', 'resourcesprogress'];

const StyledCanvas = styled.canvas`
  height: 100%;
  width: 100%;
`;

type Cursor = {
  image: string;
  isVisible: boolean;
  position: string;
  rotation: number;
  shouldUpdate: boolean;
};

export type ViewerProps = {
  actions?: { showAnimatedCursor?: (cursor: any) => void; hover?: (ids: string[]) => void };
  backgroundAlpha?: number;
  backgroundColor?: CSSProperties['color'];
  cameraControls?: 'freelook' | 'orbit' | 'pan' | 'zoomin' | 'zoomout';
  cameraDefault?: {
    defaultCameraView: {
      position: IVector3;
      rotation: IVector3;
      root: { position: IVector3; rotation: IVector3 };
    };
    cameraProps: { distance: number; fov: number; origin: IVector3; pitch: number; yaw: number };
  };
  config?: Partial<SceneViewerConfig>;
  cursor?: Cursor;
  exposure?: number;
  featureFlags?: Partial<FeatureFlagsState>;
  gizmoMode?: GizmoModes;
  hover?: string[];
  id?: string;
  isRender?: boolean;
  /**
   * @deprecated Used only by Author to track scene loading progress.
   */
  onDrawEvent?: Function;
  onUpdate?: Function;
  postprocessing?: string[];
  preserveDrawingBuffer?: boolean;
  scene: SceneDesc;
  selection?: string[];
  showGarmentGeometry?: boolean;
  theme?: Theme;
  visible?: VisibilityState;
};

export type ViewerState = {};

export class Viewer<P extends ViewerProps, S extends ViewerState> extends Component<P, S> {
  // canvas ref
  readonly canvasRef: RefObject<HTMLCanvasElement>;

  readonly config: SceneViewerConfig;

  // TODO: is this property used anywhere or just exposed to the public interface?
  readonly viewport: Viewport;

  scene: Scene;
  renderGroup: Object3D;
  renderAmbient: AmbientLight;
  camera: Camera;
  gfx: ThreeRenderer;

  cursor: Cursor;
  pointer: Pointer;
  tool: ITool;

  needsUpdate: boolean;
  onDrawEventsStack: Function[] = [];
  outlined: Object3D[];

  resources: ResourcesContext;

  protected isMounted: boolean;
  protected sceneReconciler: SceneReconciler;
  protected sceneRenderable: SceneRenderable;

  protected handleResourceChange: EventListener;

  private backgroundAlpha: number;
  private readonly backgroundColor: Color;

  private readonly clock: Clock;
  private resizeObserver: ResizeObserver | null;

  private composer: EffectComposer;
  private mainPass: RenderPass;
  private outlinePass: ThreeOutlinePass;
  private gammaCorrectionPass: ShaderPass;

  constructor(props: P) {
    super(props);

    this.canvasRef = createRef<HTMLCanvasElement>();

    this.backgroundAlpha = 1;
    this.backgroundColor = new Color(0xffffff);

    // TODO: config and featureFlags seem to be two flavors for the same coin
    this.config = createConfig(props.config);
    // TODO: instance initialization affects globally the flags state which can be confusing
    FlagsModule.init(props.featureFlags);

    this.viewport = new Viewport(0, 0, 500, 500);

    this.isMounted = false;
    this.outlined = [];

    this.setCursor = this.setCursor.bind(this);

    // init clock
    this.clock = new Clock();
    // init resize observer
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { contentBoxSize, devicePixelContentBoxSize } = entry;
        // Limited availability
        // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/devicePixelContentBoxSize
        if (devicePixelContentBoxSize) {
          const size = devicePixelContentBoxSize[0];
          log.debug('device pixel content box size', size.inlineSize, size.blockSize);
        }

        const size = contentBoxSize[0];
        this.setSize(size.inlineSize, size.blockSize);
      }
    });

    this.handleResourceChange = ((event: ResourceEvent) => {
      if (event.kind === 'Environment') {
        this.updateBackground();
      }

      // TODO: remove
      // used by Author to track scene loading progress
      if (this.props.onDrawEvent) {
        const onDrawEvent = this.props.onDrawEvent;
        this.onDrawEventsStack.push(() => onDrawEvent({ id: event.id }));
      }
    }) as EventListener;
  }

  componentDidMount() {
    const canvas = this.canvasRef.current!;
    const {
      props: { actions, backgroundAlpha, backgroundColor, scene, showGarmentGeometry, theme, visible },
    } = this;

    // configure background alpha
    if (backgroundAlpha !== undefined) {
      this.backgroundAlpha = Math.max(Math.min(backgroundAlpha, 1), 0);
    }

    // configure background color
    if (backgroundColor) {
      this.backgroundColor.set(backgroundColor);
    } else {
      // configure background color from parent
      const parent = canvas.parentNode as Element;
      const parentBackgroundColor = getComputedStyle(parent).getPropertyValue('background-color');
      this.backgroundColor.set(parentBackgroundColor);
    }

    // listen to canvas resize events
    this.resizeObserver!.observe(canvas, { box: 'content-box' });

    this.initRender();

    // init camera
    const {
      cameraDefault: { defaultCameraView, cameraProps },
    } = this.props;

    let defaultCameraProps = {
      fov: cameraProps.fov,
      distance: cameraProps.distance,
      yaw: cameraProps.yaw,
      pitch: cameraProps.pitch,
      origin: cameraProps.origin,
    };

    if (!cameraProps) {
      const camera = findWithCamera(scene);
      if (camera) {
        const {
          components: {
            camera: { fov },
            transform3D: { position },
          },
        } = camera;

        defaultCameraProps = {
          fov: (fov * 180) / Math.PI,
          origin: { x: position.x, y: position.y, z: 0 },
          yaw: 0,
          pitch: 0,
          distance: position.z,
        };
      }
    }

    this.camera = new Camera(cameraProps || defaultCameraProps);
    if (defaultCameraView) {
      this.updateCamera(defaultCameraView);
    }
    this.mainPass.camera = this.camera;
    this.outlinePass.camera = this.camera;

    // init input
    this.pointer = new Pointer({ canvas });

    // init framebuffers
    // NOTE: needs to be called after camera and pointer initializetion
    this.setSize(canvas.clientWidth, canvas.clientHeight);

    // default scene
    this.renderAmbient = new AmbientLight(0x222222);
    this.renderGroup = createObject('Default', this.scene);
    this.renderGroup.add(this.renderAmbient);
    this.renderGroup.add(this.camera.root);

    // init scene renderable
    const sceneRenderable = createSceneRenderable(this.scene);
    this.sceneRenderable = sceneRenderable;

    // init scene reconciler
    const sceneReconcilerConfig = {
      frustumCulling: this.config.frustumCulling,
      hideStitches: this.config.hideStitches,
      useStitchesDecals: this.config.useStitchesDecals,
    };
    const sceneReconciler = new SceneReconciler(
      this.resources,
      this.sceneRenderable,
      sceneReconcilerConfig,
      theme,
    );
    this.sceneReconciler = sceneReconciler;

    // listen to resource management events
    // NOTE: registration order matters, keep this one after scene reconciler constructor
    this.resources.addEventListener('resourcechange', this.handleResourceChange);

    sceneReconciler.update(scene, showGarmentGeometry);
    sceneReconciler.updateVisible(visible, this.outlined);
    this.resources.commit();

    // reset cursor on sceneLoad
    actions.showAnimatedCursor?.({});

    this.isMounted = true;

    this.gfx.renderer.setAnimationLoop(this.onAnimationTick.bind(this));
  }

  shouldComponentUpdate(nextProps: Readonly<P>) {
    const { props } = this;

    if (nextProps.exposure !== props.exposure) {
      this.setExposure(nextProps.exposure);
    }

    let isUpdate = false;

    if (this.isSceneChanged(nextProps, props)) {
      this.needsUpdate = this.updateScene(nextProps, props) || this.needsUpdate;
      // Force update when resource request is pending
      isUpdate = this.resources.hasLoadPending() || isUpdate;
    }

    if (this.isBackgroundChanged(nextProps, props)) {
      this.updateBackground(nextProps);
      isUpdate = true;
    }

    this.needsUpdate = isUpdate || this.needsUpdate;

    return isUpdate;
  }

  componentDidUpdate(_prevProps: P, _prevState: S) {
    this.resources.commit();
  }

  componentWillUnmount() {
    // clean up subscriptions
    this.resizeObserver!.disconnect();
    this.resizeObserver = null;

    this.tool && this.tool.dispose();
    this.tool = undefined;

    this.pointer && this.pointer.dispose();
    this.pointer = undefined;

    this.resources && this.resources.removeEventListener('resourcechange', this.handleResourceChange);
    this.resources && this.resources.dispose();
    this.resources = undefined;

    // TODO: this should go with scene reconciler dispose
    const geometryCache = SceneGeometryModule.instance.geometryCache;
    geometryCache.dispose();

    this.gfx?.renderer?.setAnimationLoop(null);
    this.gfx?.renderer?.dispose();
    this.gfx?.renderer?.forceContextLoss();
  }

  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    assert(EventTypes.includes(type), 'Unrecognized event type');
    switch (type) {
      case 'resourcechange':
      case 'resourcesprogress': {
        this.resources.addEventListener(type, callback, options);
        break;
      }
    }
  }

  removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void {
    assert(EventTypes.includes(type), 'Unrecognized event type');
    switch (type) {
      case 'resourcechange':
      case 'resourcesprogress': {
        this.resources.removeEventListener(type, callback, options);
        break;
      }
    }
  }

  onAnimationTick() {
    const { clock, pointer, resources, sceneReconciler, setCursor, tool } = this;

    const delta = clock.getDelta();
    const time = clock.oldTime / 1000;

    const toolneedsUpdate = tool.onAnimationTick({
      delta,
      time,
      pointer: {
        ...pointer.getFrame(),
        setCursor,
      },
    });

    // Draw from resources.
    if (resources.needsUpdate) {
      this.needsUpdate = true;
      resources.needsUpdate = false;
    }

    if (sceneReconciler.needsUpdate) {
      this.needsUpdate = true;
      sceneReconciler.needsUpdate = false;
    }

    // Draw from tool.
    if (toolneedsUpdate) {
      this.needsUpdate = true;
    }

    if (!this.needsUpdate) {
      return;
    }

    this.draw();

    const { onUpdate } = this.props;
    if (onUpdate) {
      onUpdate();
    }

    this.onDraw();
  }

  setSize(width: number, height: number) {
    const devicePixelRatio = window.devicePixelRatio;
    // update renderer
    this.gfx.setSize(width, height, devicePixelRatio);
    // update camera projection matrix
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    // update renderer composition manager
    const displayWidth = Math.round(width * devicePixelRatio);
    const displayHeight = Math.round(height * devicePixelRatio);
    log.debug('set size', displayWidth, displayHeight);
    this.composer.setSize(displayWidth, displayHeight);
    // update other stuff
    this.pointer.setSize(width, height);
    this.viewport.set(0, 0, displayWidth, displayHeight);
    this.needsUpdate = true;
  }

  downloadFrame(minSize = 2048) {
    // Get canvas aspect ratio
    const aspectRatio = this.viewport.width / this.viewport.height;

    // Calculate a target size fort exportFrame with the smallest canvas side being 'minSize'
    let targetWidth = 0;
    let targetHeight = 0;
    if (this.viewport.width < this.viewport.height) {
      targetWidth = minSize;
      targetHeight = Math.round(minSize / aspectRatio);
    } else {
      targetWidth = Math.round(minSize * aspectRatio);
      targetHeight = minSize;
    }

    const blobURL = this.exportFrame(targetWidth, targetHeight);
    const a = document.createElement('a');
    a.href = blobURL;
    if (!(window as any).chrome) {
      document.body.appendChild(a);
    }
    a.download = `webgl-${Date.now()}.png`;
    a.click();
    a.remove();
    URL.revokeObjectURL(blobURL);
  }

  draw() {
    if (!this.isMounted) {
      return;
    }

    this.gfx.renderer.clear();
    this.composer.render();

    this.needsUpdate = false;
  }

  onDraw() {
    this.onDrawEventsStack.forEach((callback) => callback());
    this.onDrawEventsStack = [];
  }

  getCamera() {
    const { camera } = this;
    const translation = camera.getWorldPosition(camera.position.clone());
    const rotation = camera.getWorldQuaternion(camera.quaternion.clone());
    return {
      transform: {
        translation: {
          x: translation.x,
          y: translation.y,
          z: translation.z,
        },
        rotation: {
          x: rotation.x,
          y: rotation.y,
          z: rotation.z,
          w: rotation.w,
        },
      },
      type: 'perspective',
      near: camera.near,
      far: camera.far,
      fov: (camera.fov * Math.PI) / 180,
    };
  }

  getDecomposedCamera() {
    const { camera } = this;
    return {
      position: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
      rotation: {
        x: camera.rotation.x,
        y: camera.rotation.y,
        z: camera.rotation.z,
      },
      root: {
        position: {
          x: camera.root.position.x,
          y: camera.root.position.y,
          z: camera.root.position.z,
        },
        rotation: {
          x: camera.root.rotation.x,
          y: camera.root.rotation.y,
          z: camera.root.rotation.z,
        },
      },
    };
  }

  resetCamera() {
    const { camera } = this;
    camera.reset();
    this.needsUpdate = true;
  }

  setCursor(cursor) {
    const { actions, cursor: prevCursor } = this.props;
    if (
      cursor.image &&
      (cursor.image !== prevCursor?.image ||
        (cursor.rotation !== prevCursor?.rotation && (cursor.rotation || prevCursor?.rotation))) &&
      this.pointer.hover
    ) {
      actions.showAnimatedCursor?.(cursor);
      this.cursor = cursor;
    }
  }

  setExposure(exposure: number) {
    this.gfx.renderer.toneMapping = CustomToneMapping;
    this.gfx.renderer.toneMappingExposure = exposure;

    this.scene.traverse((child) => {
      if ((child as any).material) {
        (child as any).material.needsUpdate = true;
      }
    });

    this.needsUpdate = true;
  }

  updateCamera({ root, position, rotation }) {
    const { camera } = this;
    camera.root.position.copy(root.position);
    camera.root.rotation.set(root.rotation.x, root.rotation.y, root.rotation.z);
    camera.position.copy(position);
    camera.rotation.set(rotation.x, rotation.y, rotation.z);
    camera.root.updateMatrixWorld();
    this.needsUpdate = true;
  }

  handleMouseLeave() {
    this.props.actions.hover?.([]);
  }

  render() {
    // TODO: draw should trigger with a mechanism independent from React
    if (this.needsUpdate) {
      this.draw();
    }

    const { id } = this.props;
    return <StyledCanvas id={id} ref={this.canvasRef} onMouseLeave={() => this.handleMouseLeave()} />;
  }

  exportFrame(width = 2048, height = 2048, quality = 0): string {
    const { gfx, scene } = this;

    // Save renderer state
    const prevTarget = gfx.renderer.getRenderTarget();
    const prevViewport = gfx.renderer.getViewport(new Vector4());
    const prevScissor = gfx.renderer.getScissor(new Vector4());
    const prevScissorTest = gfx.renderer.getScissorTest();

    // 1. Create export render target
    const depthTexture = new DepthTexture(width, height, FloatType);
    const exportTarget = new WebGLRenderTarget(width, height, {
      colorSpace: SRGBColorSpace,
      depthTexture,
      format: RGBAFormat,
      samples: 4,
    });

    // 2. Use a completely separate camera
    const exportCamera = this.makeExportCamera(width, height);

    // 3. Build a temporary composer that never touches `this.composer`
    const exportComposer = new EffectComposer(gfx.renderer, exportTarget);
    exportComposer.addPass(new RenderPass(scene, exportCamera));
    exportComposer.addPass(new ThreeOutlinePass(gfx.renderer, scene));
    exportComposer.addPass(new ShaderPass(GammaCorrectionShader));

    // 4. Render into the export target
    gfx.renderer.setRenderTarget(exportTarget);
    gfx.renderer.setViewport(0, 0, width, height);
    gfx.renderer.setScissor(0, 0, width, height);
    gfx.renderer.setScissorTest(true);
    exportComposer.render();

    // 5. Read pixels into canvas
    const pixels = new Uint8Array(width * height * 4);
    gfx.renderer.readRenderTargetPixels(exportTarget, 0, 0, width, height, pixels);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.createImageData(width, height);

    // Flip Y
    for (let y = 0; y < height; y++) {
      const srcStart = (height - y - 1) * width * 4;
      const destStart = y * width * 4;
      imageData.data.set(pixels.subarray(srcStart, srcStart + width * 4), destStart);
    }
    ctx.putImageData(imageData, 0, 0);

    // 6. Restore renderer state
    gfx.renderer.setRenderTarget(prevTarget);
    gfx.renderer.setViewport(prevViewport);
    gfx.renderer.setScissor(prevScissor);
    gfx.renderer.setScissorTest(prevScissorTest);

    this.needsUpdate = true;
    this.render();

    if (quality) {
      return canvas.toDataURL('image/png', quality);
    }
    return canvas.toDataURL('image/png');
  }

  private makeExportCamera(width: number, height: number): PerspectiveCamera {
    // Clone the live camera
    const exportCamera = this.camera.clone() as PerspectiveCamera;

    // Update aspect ratio to match export resolution
    exportCamera.aspect = width / height;
    exportCamera.updateProjectionMatrix();

    return exportCamera;
  }

  protected initRender() {
    const {
      backgroundAlpha,
      config,
      props: { exposure, preserveDrawingBuffer },
    } = this;

    // Initialize renderer.
    const gfx = new ThreeRenderer();
    {
      const canvas = this.canvasRef.current!;
      gfx.configurePreserveDrawingBuffer(preserveDrawingBuffer);
      gfx.configureAlpha(backgroundAlpha !== 1);
      gfx.init(canvas);
      this.gfx = gfx;
    }
    // Initialize resources context.
    {
      const resourcesOptions = { frustumCulling: config.frustumCulling };
      const resources = new ResourcesContext(gfx, resourcesOptions);
      this.resources = resources;
    }
    // Initialize scene.
    const isDepthPeeling = FlagsModule.isEnabled(FeatFlag.DEPTH_PEELING);
    const scene = new Scene();
    {
      if (isDepthPeeling) {
        const depthPeelingLayers = FlagsModule.getValue(FeatFlag.DEPTH_PEELING) as number;
        (scene as any).depthPeelingLayers = depthPeelingLayers;
        (scene as any).depthPeelingBias = 0;
      }
      this.scene = scene;
    }

    // Initialize renderer compositor.
    {
      // Force DEPTH_COMPONENT32F.
      const depthTexture = new DepthTexture(0, 0, FloatType);
      const renderTarget = new WebGLRenderTarget(0, 0, {
        colorSpace: SRGBColorSpace,
        depthTexture,
        format: RGBAFormat,
        samples: 4,
      });
      const composer = new EffectComposer(gfx.renderer, renderTarget);
      this.mainPass = isDepthPeeling ? new ThreeMainRenderPass(this.scene) : new RenderPass(this.scene);
      this.outlinePass = new ThreeOutlinePass(gfx.renderer, this.scene);
      this.gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
      composer.addPass(this.mainPass);
      composer.addPass(this.outlinePass);
      composer.addPass(this.gammaCorrectionPass);
      this.composer = composer;
    }

    gfx.setClearColor(this.backgroundColor, backgroundAlpha);
    this.updateBackground();

    this.setExposure(exposure);
  }

  protected isBackgroundChanged(nextProps: Readonly<P>, props: Readonly<P>) {
    if (nextProps.isRender !== props.isRender) {
      return true;
    }
    if (nextProps.visible.background !== props.visible.background) {
      return true;
    }
    if (nextProps.isRender && nextProps.scene?.children?.length) {
      const nextEntity = nextProps.scene.children.find((e) => e.components.environment);
      const prevEntity = props.scene.children.find((e) => e.components.environment);
      if (
        nextEntity?.components.environment.environment?.id !==
        prevEntity?.components.environment.environment?.id
      ) {
        return true;
      }
    }
    return false;
  }

  protected isSceneChanged(nextProps: Readonly<P>, props: Readonly<P>) {
    return (
      nextProps.scene !== props.scene ||
      nextProps.hover !== props.hover ||
      nextProps.selection !== props.selection ||
      nextProps.visible !== props.visible ||
      nextProps.showGarmentGeometry !== props.showGarmentGeometry ||
      nextProps.gizmoMode !== props.gizmoMode
    );
  }

  protected updateScene(nextProps: Readonly<P>, prevProps: Readonly<P>) {
    if (!this.isMounted) {
      return false;
    }

    const { outlined, sceneReconciler } = this;

    let dirty = false;
    let sceneDirty = false;

    // Update scene
    if (
      nextProps.scene !== prevProps.scene ||
      nextProps.showGarmentGeometry !== prevProps.showGarmentGeometry
    ) {
      sceneDirty = sceneReconciler.update(nextProps.scene, prevProps.showGarmentGeometry);
      dirty = dirty || sceneDirty;
    }

    // Update outlined renderables
    if (prevProps.hover !== nextProps.hover || prevProps.selection !== nextProps.selection || dirty) {
      const { renderables } = sceneReconciler;
      outlined.length = 0;

      for (const id of nextProps.hover) {
        if (renderables.has(id)) {
          outlined.push(renderables.get(id));
        }
      }
      for (const id of nextProps.selection) {
        if (renderables.has(id)) {
          outlined.push(renderables.get(id));
        }
      }

      dirty = true;
    }

    // Update visible elements.
    if (prevProps.visible !== nextProps.visible || sceneDirty) {
      sceneReconciler.updateVisible(nextProps.visible, outlined, nextProps.isRender);
      dirty = true;
    }

    this.outlinePass.selectedObjects = outlined;

    return dirty;
  }

  protected updateBackground(nextProps?: Readonly<P>) {
    const { backgroundAlpha, backgroundColor, props, scene } = this;

    if (backgroundAlpha !== 1.0) {
      return;
    }

    nextProps = nextProps ?? props;

    if (nextProps.isRender) {
      // Render mode selectively shows background.
      scene.background = nextProps.visible.background ? scene.environment : backgroundColor;
    } else {
      // Default viewport never shows background.
      scene.background = backgroundColor;
    }

    this.needsUpdate = true;
  }

  static defaultProps: Partial<ViewerProps> = {
    actions: { hover: () => {}, showAnimatedCursor: () => {} },
    cameraControls: 'orbit',
    cameraDefault: {
      cameraProps: { fov: 20, origin: { x: 0, y: 1, z: 0 }, yaw: 0, pitch: Math.PI * -0.1, distance: 2.2 },
      defaultCameraView: {
        position: { x: 0, y: 0, z: 1.6987071125357485 },
        rotation: { x: 0, y: 0, z: 0 },
        root: { position: { x: 0, y: 1, z: 0 }, rotation: { x: -0.21915926535897923, y: 0.01, z: 0 } },
      },
    },
    cursor: {
      image:
        "data:image/svg+xml,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cg clip-path='url(%23clip0_13281_1530)'%3e%3cmask id='path-1-outside-1_13281_1530' maskUnits='userSpaceOnUse' x='-1' y='0' width='18' height='16' fill='black'%3e%3crect fill='white' x='-1' width='18' height='16'/%3e%3cpath d='M1.68435 1.05519L14.7529 7.90637C15.1189 8.09826 15.0673 8.63146 14.671 8.7513L9.41576 10.3409C9.30803 10.3735 9.21593 10.4436 9.15659 10.5382L6.49458 14.7825C6.28866 15.1108 5.78937 15.0568 5.66025 14.6923L1.02733 1.6126C0.887599 1.2181 1.31058 0.859243 1.68435 1.05519Z'/%3e%3c/mask%3e%3cpath d='M1.68435 1.05519L14.7529 7.90637C15.1189 8.09826 15.0673 8.63146 14.671 8.7513L9.41576 10.3409C9.30803 10.3735 9.21593 10.4436 9.15659 10.5382L6.49458 14.7825C6.28866 15.1108 5.78937 15.0568 5.66025 14.6923L1.02733 1.6126C0.887599 1.2181 1.31058 0.859243 1.68435 1.05519Z' fill='%230B0B0C'/%3e%3cpath d='M1.68435 1.05519L1.22003 1.94086L1.22003 1.94086L1.68435 1.05519ZM14.7529 7.90637L15.2172 7.0207L15.2172 7.0207L14.7529 7.90637ZM14.671 8.7513L14.9606 9.70847L14.671 8.7513ZM9.41576 10.3409L9.12625 9.3837L9.12624 9.3837L9.41576 10.3409ZM9.15659 10.5382L10.0038 11.0695L10.0038 11.0695L9.15659 10.5382ZM6.49458 14.7825L5.64742 14.2511V14.2511L6.49458 14.7825ZM5.66025 14.6923L4.71763 15.0262L4.71764 15.0262L5.66025 14.6923ZM1.02733 1.6126L0.0847181 1.94648L0.0847183 1.94648L1.02733 1.6126ZM1.22003 1.94086L14.2886 8.79204L15.2172 7.0207L2.14866 0.169519L1.22003 1.94086ZM14.2886 8.79204C13.8555 8.565 13.9277 7.9314 14.3815 7.79413L14.9606 9.70847C16.2068 9.33152 16.3823 7.63151 15.2172 7.0207L14.2886 8.79204ZM14.3815 7.79413L9.12625 9.3837L9.70528 11.298L14.9606 9.70847L14.3815 7.79413ZM9.12624 9.3837C8.78932 9.48561 8.49823 9.70582 8.30943 10.0068L10.0038 11.0695C9.93362 11.1813 9.82673 11.2613 9.70529 11.298L9.12624 9.3837ZM8.30943 10.0068L5.64742 14.2511L7.34174 15.3138L10.0038 11.0695L8.30943 10.0068ZM5.64742 14.2511C5.88237 13.8765 6.45051 13.9283 6.60286 14.3584L4.71764 15.0262C5.12824 16.1854 6.69495 16.3451 7.34174 15.3138L5.64742 14.2511ZM6.60287 14.3584L1.96995 1.27872L0.0847183 1.94648L4.71763 15.0262L6.60287 14.3584ZM1.96995 1.27872C2.14672 1.77778 1.6172 2.14908 1.22003 1.94086L2.14866 0.169519C1.00396 -0.430591 -0.371521 0.658423 0.0847181 1.94648L1.96995 1.27872Z' fill='white' mask='url(%23path-1-outside-1_13281_1530)'/%3e%3c/g%3e%3cdefs%3e%3cclipPath id='clip0_13281_1530'%3e%3crect width='24' height='24' fill='white'/%3e%3c/clipPath%3e%3c/defs%3e%3c/svg%3e",
      isVisible: true,
      position: 'upperLeft',
      rotation: 0,
      shouldUpdate: false,
    },
    exposure: 0,
    gizmoMode: GizmoModes.NONE,
    hover: [],
    id: 'Viewer',
    isRender: false,
    postprocessing: [],
    preserveDrawingBuffer: false,
    selection: [],
    theme: { threads: [], buttonElements: [] },
    showGarmentGeometry: false,
    visible: {
      avatar: true,
      background: true,
      garmentPiece: true,
      garmentSeam: true,
      garmentHem: true,
      ground: true,
      logos: true,
    },
  };
}
